import { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

interface AvatarPickerProps {
  size?: 'md' | 'lg';
  /** Shown under the picker; omit for a bare control. */
  hint?: string;
}

const SIZES = {
  md: { box: 'w-14 h-14', icon: 'w-4 h-4', text: 'text-xl' },
  lg: { box: 'w-24 h-24', icon: 'w-6 h-6', text: 'text-3xl' },
} as const;

/**
 * The subscriber's profile picture, tap-to-replace.
 *
 * Uploading overwrites whatever LetzPlay imported — the backend stores it under
 * a per-subscriber key, so there is only ever one. Falls back to the initial
 * while no picture is set, matching how players render everywhere else.
 */
export function AvatarPicker({ size = 'md', hint }: AvatarPickerProps) {
  const { subscriber, user, setSubscriber } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const s = SIZES[size];
  const initial = subscriber?.name?.charAt(0) || user?.email?.charAt(0) || '?';

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    const reader = new FileReader();
    reader.onload = async () => {
      setIsUploading(true);
      try {
        const response = await api.auth.uploadAvatar(reader.result as string);
        if (response.success && response.data?.subscriber) {
          setSubscriber(response.data.subscriber);
        } else {
          setError(response.error?.message || 'Não foi possível enviar a foto');
        }
      } catch {
        setError('Erro de conexão');
      } finally {
        setIsUploading(false);
      }
    };
    reader.onerror = () => setError('Não foi possível ler o arquivo');
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        aria-label="Alterar foto de perfil"
        className={`${s.box} rounded-full relative group overflow-hidden shrink-0 disabled:opacity-60`}
      >
        {subscriber?.avatarUrl ? (
          <img
            src={subscriber.avatarUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <span
            className={`w-full h-full bg-gradient-to-br from-turquoise to-coral flex items-center justify-center text-white font-bold ${s.text}`}
          >
            {initial}
          </span>
        )}
        <span className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity">
          {isUploading ? (
            <Loader2 className={`${s.icon} text-white animate-spin`} />
          ) : (
            <Camera className={`${s.icon} text-white`} />
          )}
        </span>
        {isUploading && (
          <span className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className={`${s.icon} text-white animate-spin`} />
          </span>
        )}
      </button>
      {hint && !error && <p className="text-xs text-white/40">{hint}</p>}
      {error && <p className="text-xs text-coral">{error}</p>}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleSelect}
        className="hidden"
      />
    </div>
  );
}
